import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../services/apiService';
import PinGolfStandingsTable from '../components/PinGolfStandingsTable';
import PinGolfScore from '../components/PinGolfScore';
import PinGolfRules from '../components/PinGolfRules';
import { pickCurrentWeek, describeWeekStage, formatHandicap } from '../utils/pingolfScoring';
import pingolfHero from '../assets/pingolf-hero.webp';
import './pingolfShared.css';
import './pingolfHeroShared.css';
import './PinGolfLeagueSeason.css';
import { personName } from '../utils/personName';

const REFRESH_INTERVAL_MS = 15000;

const displayName = personName;

// Public, no-login season page: standings, this week's net board, and the prize structure.
// Mirrors the public RSVP page's no-auth pattern (EventRSVP.js).
function PinGolfLeagueSeason() {
  const { seasonId } = useParams();
  const [season, setSeason] = useState(null);
  const [standings, setStandings] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(null);
  const [netBoard, setNetBoard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [seasonRes, standingsRes, weeksRes] = await Promise.all([
        apiService.getPingolfSeason(seasonId),
        apiService.getPingolfSeasonStandings(seasonId),
        apiService.getPingolfSeasonWeeks(seasonId)
      ]);
      setSeason(seasonRes.data);
      setStandings(standingsRes.data);

      const week = pickCurrentWeek(weeksRes.data);
      setCurrentWeek(week);
      if (week) {
        const netBoardRes = await apiService.getPingolfWeekNetBoard(week.id);
        setNetBoard(netBoardRes.data);
      } else {
        setNetBoard([]);
      }
      setError(null);
    } catch (err) {
      setError('Unable to load this season right now.');
    } finally {
      setLoading(false);
    }
  }, [seasonId]);

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [fetchData]);

  if (loading) {
    return <div className="pl-season-page">Loading...</div>;
  }

  if (error || !season) {
    return <div className="pl-season-page">{error || 'Season not found.'}</div>;
  }

  const split = season.season_pool_split || {};

  return (
    <div className="pl-season-page">
      <div className="pl-season-content">
        <div className="pingolf-hero pl-season-hero">
          <img src={pingolfHero} alt="" className="pingolf-hero-banner" />
          <h1>{season.name}</h1>
        </div>
        <div className="pl-season-header">
          <p>{season.week_count}-week PinGolf League season - best {season.week_count - season.drop_count} of {season.week_count} weeks count</p>
        </div>

        {/* Rules lead the page: this is where "Learn more about the league" lands, and a
            player following that link wants the format explained before the boards. Returning
            visitors chasing standings collapse past it in one screen. */}
        <div className="pl-season-section pl-season-section--flush" id="rules">
          <h2>League Rules</h2>
          <p className="pl-season-section-intro">
            Everything a player needs to know, straight from this season's setup.
          </p>
          <PinGolfRules season={season} />
        </div>

        <div className="pl-season-section">
          <h2>{currentWeek ? `Week ${currentWeek.week_number} Net Board (${describeWeekStage(currentWeek)})` : 'Weekly Net Board'}</h2>
          {netBoard.length === 0 && <div className="pl-season-empty">No cards submitted yet this week.</div>}
          {netBoard.length > 0 && (
            <table className="pl-season-table">
              <thead><tr><th>#</th><th>Player</th><th>Card</th><th>Handicap</th><th>Net</th></tr></thead>
              <tbody>
                {netBoard.map(entry => (
                  <tr key={entry.user_id}>
                    <td>{entry.place}</td>
                    <td>{displayName(entry)}</td>
                    <td><PinGolfScore value={entry.total_score} /></td>
                    <td>{formatHandicap(entry.playing_handicap)}</td>
                    <td><PinGolfScore value={entry.net} emphasis /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="pl-season-section">
          <h2>Season Standings{standings.some(entry => entry.provisional) && <span className="pl-live-pill">Live</span>}</h2>
          {standings.length === 0 && <div className="pl-season-empty">No cards turned in yet.</div>}
          {standings.length > 0 && (
            <PinGolfStandingsTable
              standings={standings}
              weekCount={season.week_count}
              dropCount={season.drop_count}
            />
          )}
        </div>

        <div className="pl-season-section">
          <h2>Season Prize Pool</h2>
          <div className="pl-season-prizes">
            <div className="pl-season-prize-card">
              <div className="amount">{Math.round((split.champion || 0) * 100)}%</div>
              <div>Champion + trophy</div>
            </div>
            <div className="pl-season-prize-card">
              <div className="amount">{Math.round((split.runner_up || 0) * 100)}%</div>
              <div>Runner-up</div>
            </div>
            <div className="pl-season-prize-card">
              <div className="amount">{Math.round((split.third || 0) * 100)}%</div>
              <div>Third</div>
            </div>
            <div className="pl-season-prize-card">
              <div className="amount">{Math.round((split.fourth || 0) * 100)}%</div>
              <div>Fourth</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PinGolfLeagueSeason;
